import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Receipt,
  Wallet,
  Database,
  Settings,
  LogOut,
  Menu,
  X,
  Users,
  Heart,
  Wrench,
  FileText,
  UserCog,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { ScrollArea } from "../../components/ui/scroll-area";
import { cn } from "../../lib/utils";

const SuperAdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("superAdminToken");
    const userData = localStorage.getItem("superAdminUser");

    if (!token) {
      navigate("/super-admin/login");
      return;
    }

    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("superAdminToken");
    localStorage.removeItem("superAdminUser");
    navigate("/super-admin/login");
  };

  const menuItems = [
    {
      path: "/super-admin/dashboard",
      label: "لوحة التحكم",
      icon: LayoutDashboard,
    },
    { path: "/super-admin/tenants", label: "المستأجرين", icon: Building2 },
    { path: "/super-admin/plans", label: "الخطط", icon: CreditCard },
    { path: "/super-admin/subscriptions", label: "الاشتراكات", icon: Users },
    { path: "/super-admin/invoices", label: "الفواتير", icon: Receipt },
    { path: "/super-admin/payments", label: "المدفوعات", icon: Wallet },
    { path: "/super-admin/backups", label: "النسخ الاحتياطية", icon: Database },
    
    // System Management Section
    {
      label: "إدارة النظام",
      type: "section",
      items: [
        { path: "/super-admin/system/health", label: "حالة النظام", icon: Heart },
        { path: "/super-admin/system/maintenance", label: "صيانة النظام", icon: Wrench },
        { path: "/super-admin/system/users", label: "مستخدمو النظام", icon: UserCog },
        { path: "/super-admin/system/logs", label: "سجل النشاطات", icon: FileText },
      ]
    },
    
    { path: "/super-admin/settings", label: "الإعدادات", icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800"
      dir="rtl"
    >
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 right-0 z-40 h-screen transition-transform bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 shadow-xl",
          sidebarOpen ? "translate-x-0" : "translate-x-full",
          "w-64"
        )}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                Super Admin
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            {user && (
              <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <UserCog className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                  <span className="text-sm font-medium text-slate-800 dark:text-white">
                    {user.full_name || user.username}
                  </span>
                </div>
                <Button 
                  onClick={() => navigate("/super-admin/profile")}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white p-0 h-auto"
                >
                  <span className="text-xs">تعديل الملف الشخصي</span>
                </Button>
              </div>
            )}
          </div>

          {/* Menu Items */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-1">
              {menuItems.map((item, index) => {
                // Handle section headers
                if (item.type === "section") {
                  return (
                    <div key={index} className="px-4 py-2">
                      <h3 className="text-xs uppercase font-semibold text-slate-500 dark:text-slate-400 tracking-wider mb-2">
                        {item.label}
                      </h3>
                      <div className="space-y-1">
                        {item.items.map((subItem) => {
                          const Icon = subItem.icon;
                          const active = isActive(subItem.path);
                          
                          return (
                            <button
                              key={subItem.path}
                              onClick={() => navigate(subItem.path)}
                              className={cn(
                                "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-right",
                                active
                                  ? "bg-gradient-to-l from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30"
                                  : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                              )}
                            >
                              <span className="flex-1 text-right">{subItem.label}</span>
                              <Icon
                                className={cn(
                                  "h-4 w-4 flex-shrink-0",
                                  active
                                    ? "text-white"
                                    : "text-slate-500 dark:text-slate-400"
                                )}
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                
                // Handle regular menu items
                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-right",
                      active
                        ? "bg-gradient-to-l from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30"
                        : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                    )}
                  >
                    <span className="flex-1 text-right">{item.label}</span>
                    <Icon
                      className={cn(
                        "h-5 w-5 flex-shrink-0",
                        active
                          ? "text-white"
                          : "text-slate-500 dark:text-slate-400"
                      )}
                    />
                  </button>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Logout Button */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-700">
            <Button
              onClick={handleLogout}
              variant="destructive"
              className="w-full flex items-center justify-center gap-2"
            >
              <span>تسجيل الخروج</span>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={cn(
          "transition-all duration-300",
          sidebarOpen ? "mr-64" : "mr-0"
        )}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center justify-between h-16 px-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-slate-700 dark:text-slate-300"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>متصل</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default SuperAdminLayout;
